import React, { useEffect, useState } from 'react';
import API from 'shared/api';
import { FiLayers, FiTrendingDown, FiTrendingUp } from 'react-icons/fi';
import { COL } from 'shared/darkColors';
import { BarsH } from 'shared/components/Dashboard/charts';
import {
  S, money, intFmt, Kpi, SectionCard, Cargando, ErrorMsg, buildQuery, descargarCSV,
} from './reportesCommon';

const ESTADO_LABEL = {
  borrador: 'Borrador',
  en_proceso: 'En proceso',
  completada: 'Completada',
  cancelada: 'Cancelada',
};

export default function ReporteProduccion({ filtros }) {
  const [tab, setTab] = useState('ordenes');
  const [ordenes, setOrdenes] = useState([]);
  const [margen, setMargen] = useState([]);
  const [mermas, setMermas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setError(false);
    setOrdenes([]);
    setMargen([]);
    setMermas([]);

    const reqs = [];
    reqs.push(
      API.get(`/produccion/ordenes?${buildQuery(filtros, { limite: 100 }).toString()}`)
        .then((r) => r.data.status === 'success' && r.data.data?.items)
        .catch(() => null)
    );
    reqs.push(
      API.get('/produccion/reportes/margen')
        .then((r) => r.data.status === 'success' && r.data.data)
        .catch(() => null)
    );
    reqs.push(
      API.get(`/produccion/reportes/mermas?${buildQuery(filtros).toString()}`)
        .then((r) => r.data.status === 'success' && r.data.data)
        .catch(() => null)
    );

    Promise.all(reqs)
      .then(([o, m, me]) => {
        if (!alive) return;
        setOrdenes(o || []);
        setMargen(m || []);
        setMermas(me || []);
      })
      .catch(() => alive && setError(true))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filtros.sede, filtros.fechaDesde, filtros.fechaHasta]);

  if (loading) return <Cargando />;
  if (error) return <ErrorMsg />;

  const totalOrdenes = ordenes.length;
  const completadas = ordenes.filter((o) => o.estado === 'completada').length;
  const enProceso = ordenes.filter((o) => o.estado === 'en_proceso').length;
  const mermaTotal = mermas.reduce((s, m) => s + Number(m.costo_total || 0), 0);

  const exportar = () => {
    if (tab === 'ordenes') {
      descargarCSV(
        'reporte_ordenes_produccion.csv',
        ['ID', 'Receta', 'Sede', 'Cantidad', 'Estado', 'Merma', 'Creado'],
        ordenes.map((o) => [o.id, o.receta_nombre, o.sede_codigo, o.cantidad, ESTADO_LABEL[o.estado] || o.estado, o.merma, o.creado_en])
      );
    } else if (tab === 'margen') {
      descargarCSV(
        'reporte_margen_produccion.csv',
        ['Producto', 'Código', 'Precio', 'Costo', 'Margen', 'Margen %'],
        margen.map((m) => [m.nombre, m.codigo, m.precio, m.costo, m.margen, m.margen_pct])
      );
    } else {
      descargarCSV(
        'reporte_mermas.csv',
        ['Categoría', 'Movimientos', 'Costo total'],
        mermas.map((m) => [m.categoria, m.movimientos, m.costo_total])
      );
    }
  };

  return (
    <div>
      <div style={S.kpiGrid}>
        <Kpi label="Órdenes" value={intFmt(totalOrdenes)} color={COL.pri} />
        <Kpi label="Completadas" value={intFmt(completadas)} color={COL.green} />
        <Kpi label="En proceso" value={intFmt(enProceso)} color={COL.amber} />
        <Kpi label="Mermas (costo)" value={money(mermaTotal)} color={COL.red} />
      </div>

      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap', marginBottom: '14px' }}>
        <button onClick={() => setTab('ordenes')} style={{ ...S.btn, ...(tab === 'ordenes' ? S.btnActive : {}) }}>Órdenes</button>
        <button onClick={() => setTab('margen')} style={{ ...S.btn, ...(tab === 'margen' ? S.btnActive : {}) }}>Costo y margen</button>
        <button onClick={() => setTab('mermas')} style={{ ...S.btn, ...(tab === 'mermas' ? S.btnActive : {}) }}>Mermas</button>
        <div style={{ flex: 1 }} />
        <button onClick={exportar} style={S.btn}>Exportar CSV</button>
      </div>

      {tab === 'ordenes' && (
        <SectionCard title="Órdenes de producción" icon={FiLayers} color={COL.pri}>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>{['#', 'Receta', 'Sede', 'Cantidad', 'Estado', 'Merma', 'Creado'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {ordenes.map((o) => (
                  <tr key={o.id}>
                    <td style={S.td}>{o.id}</td>
                    <td style={{ ...S.td, fontWeight: 600 }}>{o.receta_nombre || '—'}</td>
                    <td style={{ ...S.td, color: COL.muted }}>{o.sede_codigo}</td>
                    <td style={S.td}>{intFmt(o.cantidad)}</td>
                    <td style={S.td}>
                      <span style={{ ...S.chip, color: COL.pri2, background: COL.bg }}>{ESTADO_LABEL[o.estado] || o.estado}</span>
                    </td>
                    <td style={S.td}>{intFmt(o.merma)}</td>
                    <td style={{ ...S.td, color: COL.muted, fontSize: '11px' }}>{o.creado_en?.slice(0, 10) || '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {ordenes.length === 0 && <div style={{ textAlign: 'center', padding: '20px', color: COL.muted }}>Sin órdenes de producción</div>}
        </SectionCard>
      )}

      {tab === 'margen' && (
        <SectionCard title="Costo y margen por producto producido" icon={FiTrendingUp} color={COL.green}>
          <div style={S.tableWrap}>
            <table style={S.table}>
              <thead>
                <tr>{['Producto', 'Código', 'Precio', 'Costo', 'Margen', 'Margen %'].map((h) => <th key={h} style={S.th}>{h}</th>)}</tr>
              </thead>
              <tbody>
                {margen.map((m) => (
                  <tr key={m.codigo}>
                    <td style={{ ...S.td, fontWeight: 600 }}>{m.nombre}</td>
                    <td style={{ ...S.td, color: COL.muted }}>{m.codigo}</td>
                    <td style={S.td}>{money(m.precio)}</td>
                    <td style={{ ...S.td, color: COL.muted }}>{money(m.costo)}</td>
                    <td style={{ ...S.td, fontWeight: 700, color: Number(m.margen) >= 0 ? COL.green : COL.red }}>{money(m.margen)}</td>
                    <td style={{ ...S.td, fontWeight: 700, color: Number(m.margen) >= 0 ? COL.green : COL.red }}>{m.margen_pct}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {margen.length === 0 && <div style={{ textAlign: 'center', padding: '20px', color: COL.muted }}>Sin productos producidos</div>}
        </SectionCard>
      )}

      {tab === 'mermas' && (
        <SectionCard title="Consumo y mermas por categoría" icon={FiTrendingDown} color={COL.red}>
          <BarsH items={mermas.map((m) => ({ label: m.categoria, value: Number(m.costo_total || 0), sub: `${intFmt(m.movimientos)} movimientos` }))} fmt={money} color={COL.red} />
          {mermas.length === 0 && <div style={{ textAlign: 'center', padding: '20px', color: COL.muted }}>Sin mermas en el período</div>}
        </SectionCard>
      )}
    </div>
  );
}
